import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserRole } from '../utils/jwtHelper.js';
import EventsManagement from '../components/EventsManagement.js';
import { AuthService } from '../Services/AuthService';
import WhatsAppManagement from '../components/WhatsAppManagement';

import Statistics from '../components/Statistics';
import { API_BASE_URL, isFeatureEnabled } from '../config/environment';

const AdminPanel = () => {
  const navigate = useNavigate();
  const userRole = getUserRole();

  const baseMenuItems = useMemo(() => ([
    { id: 'whatsapp', label: 'WhatsApp', adminOnly: false, featureKey: 'whatsapp' },
    { id: 'events', label: 'Wydarzenia', adminOnly: true, featureKey: 'events' },
    { id: 'stats', label: 'Statystyki', adminOnly: true, featureKey: 'stats' }
  ]), []);

  const menuItems = useMemo(
    () => baseMenuItems.filter((item) => !item.featureKey || isFeatureEnabled(item.featureKey)),
    [baseMenuItems]
  );

  const [activeTab, setActiveTab] = useState(() => menuItems[0]?.id ?? '');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (menuItems.length > 0 && !menuItems.some((item) => item.id === activeTab)) {
      setActiveTab(menuItems[0].id);
    }
  }, [menuItems, activeTab]);

  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/verify-session`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (!response.ok || data.requireLogout) {
          await AuthService.logout();
          navigate('/');
        }
      } catch (error) {
        console.error('Token check error:', error);
        await AuthService.logout();
        navigate('/');
      }
    };

    checkToken();
    const intervalId = setInterval(checkToken, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [navigate]);

  if (menuItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-bold text-gray-900">Brak aktywnych modułów</h2>
          <p className="text-gray-600">
            Żadne moduły panelu administracyjnego nie są obecnie dostępne w tej konfiguracji.
          </p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      navigate('/');
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
      navigate('/');
    }
  };

  const renderMenuItem = (item) => {
    if (item.adminOnly && userRole !== 'admin') return null;

    return (
      <button
        key={item.id}
        onClick={() => {
          setActiveTab(item.id);
          setIsMobileMenuOpen(false);
        }}
        className={`w-full px-4 py-3 text-left rounded transition-colors duration-200
          ${activeTab === item.id ? 'bg-red-100 text-red-700' : 'hover:bg-gray-100 text-gray-700'}
          text-sm sm:text-base font-medium`}
      >
        {item.label}
      </button>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'whatsapp':
        return (
          <div>
            <h2 className="text-xl font-bold mb-4">Zarządzanie WhatsApp</h2>
            <WhatsAppManagement />
          </div>
        );
  
      case 'events':
        return userRole === 'admin' ? (
          <div>
            <h2 className="text-xl font-bold mb-4">Wydarzenia</h2>
            <EventsManagement />
          </div>
        ) : null;
  
      case 'stats':
        return userRole === 'admin' ? (
          <div>
            <h2 className="text-xl font-bold mb-4">Statystyki</h2>
            <Statistics />
          </div>
        ) : null;
  
      default:
        return (
          <div className="text-center py-8">
            <h2 className="text-xl font-bold text-gray-600">
              Wybierz opcję z menu
            </h2>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow fixed top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md hover:bg-gray-100 transition-colors duration-200"
              aria-label="Menu"
            >
              <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>

            <h1 className="text-xl font-bold text-gray-900">Panel Administracyjny</h1>
            
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 
                transition-colors duration-200"
            >
              Wyloguj
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pt-20 pb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Mobile Menu Overlay */}
          <div 
            className={`fixed inset-0 bg-black bg-opacity-50 md:hidden z-20 transition-opacity duration-300
              ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Mobile Sidebar */}
          <aside 
            className={`fixed md:hidden left-0 top-0 h-full w-64 bg-white transform transition-transform 
              duration-300 ease-in-out z-20 pt-16 shadow-lg
              ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <nav className="p-4 space-y-2">
              {menuItems.map(renderMenuItem)}
            </nav>
          </aside>

          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 bg-white rounded-lg shadow-lg p-4 h-fit sticky top-20">
            <nav className="space-y-2">
              {menuItems.map(renderMenuItem)}
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 bg-white rounded-lg shadow-lg p-4 sm:p-6">
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;